import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../services/api";
import "./hostel.css";

export default function RoomBeds() {
  const { hostelId, roomId } = useParams();
  const navigate = useNavigate();
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [beds, setBeds] = useState([]);
  const [students, setStudents] = useState([]);
  const [newBedNumber, setNewBedNumber] = useState("");
  const [selectedStudentByBed, setSelectedStudentByBed] = useState({});
  const [loading, setLoading] = useState(false);

  const bedSummary = beds.reduce(
    (summary, bed) => ({
      total: summary.total + 1,
      occupied: summary.occupied + (bed.is_occupied ? 1 : 0),
      available: summary.available + (!bed.is_occupied ? 1 : 0),
    }),
    { total: 0, occupied: 0, available: 0 }
  );

  // ================= LOAD BEDS =================
  const loadBeds = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/school-admin/rooms/${roomId}/beds/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setBeds(res.data.beds || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load beds");
    } finally {
      setLoading(false);
    }
  };

  // ================= LOAD STUDENTS =================
  const loadStudents = async () => {
    try {
      const res = await API.get(`/school-admin/students/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setStudents(res.data.students || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load students");
    }
  };

  // ================= ADD BED =================
  const addBed = async () => {
    if (!newBedNumber.trim()) return alert("Enter bed number");

    try {
      await API.post(
        `/school-admin/rooms/${roomId}/beds/`,
        { bed_number: newBedNumber },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      setNewBedNumber("");
      loadBeds();
    } catch (err) {
      console.error(err);
      alert("Failed to add bed");
    }
  };

  // ================= ALLOCATE =================
  const allocateStudent = async (bedId) => {
    const studentId = selectedStudentByBed[bedId];
    if (!studentId) return alert("Select student");

    try {
      await API.post(
        `/school-admin/hostel/allocate/`,
        {
          student: studentId,
          room: roomId,
          bed: bedId,
        },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      alert("Student allocated");
      loadBeds();
    } catch (err) {
      console.error(err);
      alert("Allocation failed");
    }
  };

  // ================= DE-ALLOCATE =================
  const deallocateStudent = async (bedId) => {
    if (!window.confirm("Remove student from this bed?")) return;

    try {
      await API.post(
        `/school-admin/hostel/deallocate/`,
        { bed_id: bedId },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      alert("Student removed");
      loadBeds();
    } catch (err) {
      console.error(err);
      alert("Failed to remove student");
    }
  };

  useEffect(() => {
    loadBeds();
    loadStudents();
    // eslint-disable-next-line
  }, [roomId]);

  return (
    <div className="hostel-page beds-page">
      {/* HEADER */}
      <div className="beds-hero">
        <div className="beds-hero-left">
          <span className="beds-hero-icon">🛏️</span>
          <div>
            <h1>Room Beds & Allocation</h1>
            <p>Manage beds and assign or remove students.</p>
          </div>
        </div>

        <div className="beds-hero-actions">
          {hostelId && (
            <button
              className="beds-back-btn"
              onClick={() => navigate(`/school-admin/hostels/${hostelId}/rooms`)}
            >
              Back to Rooms
            </button>
          )}
          <button
            className="beds-back-btn"
            onClick={() => navigate("/school-admin/hostels")}
          >
            All Hostels
          </button>
        </div>
      </div>

      <div className="beds-stats">
        <div className="beds-stat-card">
          <span>Total Beds</span>
          <strong>{bedSummary.total}</strong>
        </div>
        <div className="beds-stat-card">
          <span>Occupied</span>
          <strong>{bedSummary.occupied}</strong>
        </div>
        <div className="beds-stat-card">
          <span>Available</span>
          <strong>{bedSummary.available}</strong>
        </div>
      </div>

      {/* ADD BED */}
      {/* <div className="hostel-add-box">
        <input
          className="hostel-input"
          placeholder="Bed number (1, 2, A1)"
          value={newBedNumber}
          onChange={(e) => setNewBedNumber(e.target.value)}
        />
       
      </div> */}

      <div className="beds-section-header">
        <div>
          <h2>Bed List</h2>
          <p>
            {loading
              ? "Loading beds..."
              : `${bedSummary.available} available / ${bedSummary.occupied} occupied`}
          </p>
        </div>
      </div>

      {loading && (
        <div className="beds-loading">
          <div className="hostel-spinner" />
        </div>
      )}

      {/* BED GRID */}
      <div className="beds-grid">
        {beds.length === 0 && !loading && (
          <div className="beds-empty">
            <span>🛏️</span>
            <p>No beds added yet.</p>
            <small>Create beds from the room setup to start allocation.</small>
          </div>
        )}

        {beds.map((b) => (
          <div
            key={b.id}
            className={`beds-card ${b.is_occupied ? "occupied" : "available"}`}
          >
            <div className="beds-card-header">
              <div>
                <span>Bed</span>
                <h3>{b.bed_number}</h3>
              </div>
              <span
                className={
                  b.is_occupied ? "beds-status occupied" : "beds-status available"
                }
              >
                {b.is_occupied ? "Occupied" : "Available"}
              </span>
            </div>

            <div className="beds-card-body">
              {b.is_occupied ? (
                <>
                  <div className="beds-student-card">
                    <div className="beds-student-avatar">
                      {b.student_name?.charAt(0) || "S"}
                    </div>
                    <div className="beds-student-info">
                      <h4>{b.student_name || "Student"}</h4>
                      <p>{b.student_class || "Class N/A"} / Roll {b.student_roll || "N/A"}</p>
                    </div>
                  </div>

                  <div className="beds-info-grid">
                    <div>
                      <span>Admission No</span>
                      <strong>{b.student_admission || "N/A"}</strong>
                    </div>
                    <div>
                      <span>Class</span>
                      <strong>{b.student_class || "N/A"}</strong>
                    </div>
                  </div>

                  <button
                    className="beds-remove-btn"
                    onClick={() => deallocateStudent(b.id)}
                  >
                    Remove Student
                  </button>
                </>
              ) : (
                <>
                  <label className="beds-field">
                    <span>Assign Student</span>

                    <select
                      value={selectedStudentByBed[b.id] || ""}
                      onChange={(e) =>
                        setSelectedStudentByBed((prev) => ({
                          ...prev,
                          [b.id]: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select student</option>
                      {students.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                          {s.class_name ? ` (${s.class_name})` : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <button
                    className="beds-allocate-btn"
                    onClick={() => allocateStudent(b.id)}
                  >
                    Allocate Student
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
